import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, Scissors, Sparkles, Quote, Wand2 } from "lucide-react";
import { findMoments, clipFromRange } from "../lib/api";
import { fmt } from "../lib/format";
import { toast } from "sonner";

export default function MomentFinder({ showId, onCut }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [cutting, setCutting] = useState(null);

  const run = async (reference = false) => {
    const q = query.trim();
    if (!reference && !q) return;
    setLoading(true);
    setResults(null);
    try {
      const r = await findMoments(showId, q, reference);
      setResults(r.matches || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const cut = async (m, i) => {
    setCutting(i);
    try {
      await clipFromRange(showId, { start: m.start, end: m.end, title: m.title, caption: m.caption });
      toast.success("Cutting your clip — it'll appear in the list shortly");
      onCut?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cut clip");
    } finally {
      setCutting(null);
    }
  };

  return (
    <div className="border border-accent/30 bg-accent/[0.04] p-5" data-testid="moment-finder">
      <div className="flex items-center gap-2 text-accent">
        <Sparkles className="h-4 w-4" />
        <span className="font-mono text-[11px] uppercase tracking-[0.25em]">Find a moment</span>
      </div>
      <p className="mt-2 font-body text-sm text-muted-foreground">
        Describe the part of the show you want — e.g. <span className="text-white/80">"the conversation that deals with getting robbed"</span> — and jump straight to it.
      </p>
      <div className="mt-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="moment-finder-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") run(); }}
            placeholder="I'm looking for the bit about…"
            className="w-full border border-border bg-background py-2.5 pl-10 pr-3 font-body text-sm text-white outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <button
          data-testid="moment-finder-search"
          onClick={() => run(false)}
          disabled={loading || !query.trim()}
          className="flex h-[42px] items-center gap-2 bg-accent px-5 font-mono text-[11px] font-bold uppercase tracking-widest text-black transition-transform hover:-translate-y-0.5 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Find
        </button>
      </div>
      <button
        data-testid="moment-finder-reference"
        onClick={() => run(true)}
        disabled={loading}
        className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-accent disabled:opacity-50"
      >
        <Wand2 className="h-3.5 w-3.5" /> Or match my reference clips (Style DNA)
      </button>

      <AnimatePresence>
        {results && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 space-y-2" data-testid="moment-finder-results">
            {results.length === 0 ? (
              <p className="py-4 text-center font-mono text-xs text-muted-foreground">
                No moment matched that — try describing it differently.
              </p>
            ) : results.map((m, i) => (
              <div key={i} data-testid={`moment-result-${i}`} className="flex items-start gap-3 border border-border bg-card p-3">
                <div className="grid h-9 w-12 shrink-0 place-items-center bg-accent/15 font-mono text-[10px] font-bold text-accent">
                  {m.relevance}%
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-body text-sm font-semibold text-white">{m.title}</p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {fmt(m.start)} → {fmt(m.end)} · {Math.round(m.end - m.start)}s
                  </p>
                  {m.quote && (
                    <p className="mt-1.5 flex gap-1.5 font-body text-xs italic text-white/70">
                      <Quote className="mt-0.5 h-3 w-3 shrink-0 text-accent" />"{m.quote}"
                    </p>
                  )}
                </div>
                <button
                  data-testid={`moment-cut-${i}`}
                  onClick={() => cut(m, i)}
                  disabled={cutting !== null}
                  className="flex shrink-0 items-center gap-1.5 border border-accent px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-accent transition-colors hover:bg-accent hover:text-black disabled:opacity-50"
                >
                  {cutting === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Scissors className="h-3.5 w-3.5" />}
                  Cut clip
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
